import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useStore } from './store/useStore';
import { useAuthStore } from './store/useAuthStore';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import CartNotificationModal from './components/CartNotificationModal';

// Pages
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import PaymentSuccess from './pages/PaymentSuccess';
import Register from './pages/Register';
import Login from './pages/Login';
import Admin from './pages/Admin';
import Profile from './pages/Profile';
import Gallery from './pages/Gallery';
import DynamicPage from './pages/DynamicPage';
import ProductEditor from './components/ProductEditor';
import InstagramCallback from './pages/InstagramCallback';

import './App.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

function App() {
  const setProducts = useStore((state) => state.setProducts);
  const verifyToken = useAuthStore((state) => state.verifyToken);
  const setIsHydrating = useAuthStore((state) => state.setIsHydrating);

  useEffect(() => {
    // Verify auth token on app load
    const initializeAuth = async () => {
      await verifyToken();
      setIsHydrating(false);
    };
    initializeAuth();

    // Load products from database API
    const loadProducts = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/products/with-images`);
        if (response.ok) {
          const productsData = await response.json();
          // Transform products to match store format
          const formattedProducts = productsData.map((product) => ({
            ...product,
            image: product.images && product.images.length > 0
              ? product.images[0].image_url
              : 'https://via.placeholder.com/300?text=No+Image',
          }));
          setProducts(formattedProducts);
        } else {
          console.error('Failed to fetch products');
          // Fallback to empty products array
          setProducts([]);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
        // Fallback to empty products array
        setProducts([]);
      }
    };

    loadProducts();
  }, [setProducts, verifyToken, setIsHydrating]);

  return (
    <Router>
      <div className="App">
        <Header />
        <CartNotificationModal />
        <main className="main-content">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/products" element={<Products />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-result" element={<PaymentSuccess />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/about" element={<DynamicPage pageId="about" />} />
            <Route path="/contact" element={<DynamicPage pageId="contact" />} />
            <Route path="/delivery" element={<DynamicPage pageId="delivery" />} />
            <Route path="/returns" element={<DynamicPage pageId="returns" />} />
            <Route path="/page/:pageId" element={<DynamicPage />} />
            <Route path="/register" element={<Register />} />
            <Route path="/login" element={<Login />} />
            <Route path="/auth/instagram/callback" element={<InstagramCallback />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin/product/:productId" element={<ProductEditor />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
